import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, ChevronRight, Shield, Star, MapPin, Users, Briefcase, BookOpen, Tag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCmsTheme } from '@/context/CmsThemeContext';
import { useSiteContent } from '@/context/SiteContentContext';
import { cn } from '@/lib/utils';
import DynamicIcon from '@/components/common/DynamicIcon';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { useIndustrySlug } from '@/hooks/useIndustrySlug';

interface NavItem {
  id: string;
  label: string;
  type: 'link' | 'dropdown';
  href?: string;
  icon?: string | null;
  visible: boolean;
}

interface NavConfig {
  utility_bar: boolean;
  utility_badges: string[];
  items: NavItem[];
  cta_text: string;
  cta_style: string;
  show_phone_in_nav: boolean;
}

interface NavPage {
  id: string;
  title: string;
  slug: string;
  parent_id: string | null;
  show_in_nav: boolean;
  nav_order: number;
  is_published: boolean;
}

const defaultNavConfig: NavConfig = {
  utility_bar: true,
  utility_badges: ['Licensed & Insured', '5-Star Rated'],
  items: [
    { id: 'services', label: 'Services', type: 'dropdown', icon: 'Wrench', visible: true },
    { id: 'areas', label: 'Service Areas', type: 'dropdown', icon: 'MapPin', visible: true },
    { id: 'offers', label: 'Promos', type: 'link', href: '/promos', icon: 'Tag', visible: true },
    { id: 'about', label: 'About', type: 'dropdown', icon: null, visible: true },
    { id: 'contact', label: 'Contact', type: 'link', href: '/contact', icon: null, visible: true },
  ],
  cta_text: 'Call Now',
  cta_style: 'primary',
  show_phone_in_nav: true,
};

/**
 * CMS-mode themed header that reads nav_config and renders full mega menu dropdowns.
 * Adapts its chrome (utility bar, layout) based on cms_theme_config.header_style.
 */
export const CmsThemedHeader: React.FC = () => {
  const { config } = useCmsTheme();
  const ctx = useSiteContent();
  const industrySlug = useIndustrySlug();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsMobileOpen(false); setActiveMenu(null); }, [location.pathname]);

  const templateCompanyName = ctx.template.brand?.name || 'Company';
  const templatePhone = ctx.template.contact?.phone || '';

  const { data: bizSettings } = useQuery({
    queryKey: ['business-settings-header'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('logo_url, company_name, phone')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const logoUrl = bizSettings?.logo_url || null;
  const companyName = bizSettings?.company_name || templateCompanyName;
  const phone = bizSettings?.phone || templatePhone;

  // ─── Data queries (same as MegaMenu) ───
  const { data: navConfig } = useQuery({
    queryKey: ['nav-config'],
    queryFn: async () => {
      const { data } = await pmsFrom('site_content').select('nav_config').order('updated_at', { ascending: false }).limit(1).maybeSingle();
      if (data?.nav_config && typeof data.nav_config === 'object') {
        const raw = data.nav_config as any;
        return { ...defaultNavConfig, ...raw, items: Array.isArray(raw.items) ? raw.items : defaultNavConfig.items, utility_badges: Array.isArray(raw.utility_badges) ? raw.utility_badges : defaultNavConfig.utility_badges } as NavConfig;
      }
      return defaultNavConfig;
    },
    staleTime: 1000 * 60 * 5,
  });

  const cfg = navConfig || defaultNavConfig;
  const visibleItems = cfg.items.filter(i => i.visible);

  const { data: dbSegments = [] } = useQuery({
    queryKey: ['mega-segments'],
    queryFn: async () => { const { data } = await pmsFrom('service_segments').select('id, name, slug, icon').eq('is_active', true).order('sort_order'); return data || []; },
    staleTime: 1000 * 60 * 5,
  });

  const { data: dbCategories = [] } = useQuery({
    queryKey: ['mega-categories'],
    queryFn: async () => { const { data } = await pmsFrom('service_categories').select('id, name, slug, icon, segment_id').eq('is_active', true).order('sort_order'); return data || []; },
    staleTime: 1000 * 60 * 5,
  });

  const { data: dbRegions = [] } = useQuery({
    queryKey: ['mega-regions'],
    queryFn: async () => { const { data } = await pmsFrom('service_regions').select('id, name, slug, icon').eq('is_active', true).order('sort_order'); return data || []; },
    staleTime: 1000 * 60 * 5,
  });

  const { data: dbLocations = [] } = useQuery({
    queryKey: ['mega-locations'],
    queryFn: async () => { const { data } = await pmsFrom('service_locations').select('name, slug, region, region_id').eq('is_published', true).order('sort_order'); return data || []; },
    staleTime: 1000 * 60 * 5,
  });

  const { data: dbPromotions = [] } = useQuery({
    queryKey: ['mega-promotions'],
    queryFn: async () => { const { data } = await pmsFrom('promotions').select('id, title, description, discount_type, discount_value, promo_code, banner_text').eq('is_active', true); return data || []; },
    staleTime: 1000 * 60 * 5,
  });

  const { data: navPages = [] } = useQuery({
    queryKey: ['nav-pages'],
    queryFn: async () => { const { data } = await pmsFrom('pages').select('id, title, slug, parent_id, show_in_nav, nav_order, is_published').eq('show_in_nav', true).eq('is_published', true).order('nav_order'); return (data || []) as NavPage[]; },
  });

  const dynamicPages = useMemo(() => {
    const staticSlugs = ['home', 'contact', 'about', 'services', 'service-areas', 'resources', 'careers', 'blog'];
    return navPages.filter(p => !staticSlugs.includes(p.slug) && !p.parent_id);
  }, [navPages]);

  const closeMenu = () => { setActiveMenu(null); setIsMobileOpen(false); setHoveredRegion(null); };
  const isActive = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) => location.pathname.startsWith(prefix);

  const dropdownClasses = "absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-[100]";
  const dropdownItemClasses = "flex items-start gap-3 p-3 rounded-lg hover:bg-muted/60 transition-all duration-200 group";

  // ─── Dropdown renderers ───
  const renderServicesDropdown = () => (
    <div className="p-4">
      {dbSegments.length > 0 ? (
        <div className="space-y-3">
          {dbSegments.map((seg: any) => {
            const segCats = dbCategories.filter((c: any) => c.segment_id === seg.id);
            return (
              <div key={seg.id}>
                <Link to={`/${seg.slug}`} onClick={closeMenu} className="flex items-center gap-2 px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-primary transition-colors">
                  <DynamicIcon name={seg.icon || 'Layers'} className="h-3.5 w-3.5" />
                  {seg.name}
                </Link>
                <div className="grid grid-cols-2 gap-0.5 mt-1">
                  {segCats.map((cat: any) => (
                    <Link key={cat.id} to={`/${seg.slug}/${cat.slug}`} onClick={closeMenu} className={dropdownItemClasses}>
                      <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-all">
                        <DynamicIcon name={cat.icon || 'Wrench'} className="h-3.5 w-3.5 text-primary group-hover:text-primary-foreground transition-colors" />
                      </div>
                      <span className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">{cat.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground p-4 text-center">No services configured yet</div>
      )}
      <div className="mt-3 pt-3 border-t border-border/50">
        <Link to="/services" onClick={closeMenu} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors group">
          <span className="text-sm font-semibold text-primary">View All Services</span>
          <ChevronRight className="h-4 w-4 text-primary group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  );

  const renderAreasDropdown = () => {
    const regionsWithLocs = dbRegions.filter((r: any) => dbLocations.some((l: any) => l.region_id === r.id));
    const activeRegionId = hoveredRegion || (regionsWithLocs.length > 0 ? regionsWithLocs[0].id : null);
    const activeRegion = regionsWithLocs.find((r: any) => r.id === activeRegionId);
    const regionLocs = activeRegionId ? dbLocations.filter((l: any) => l.region_id === activeRegionId) : [];
    const displayLocs = regionLocs.slice(0, 12);
    const hasMoreLocs = regionLocs.length > 12;
    const highlightPromo = dbPromotions.length > 0 ? dbPromotions[0] : null;

    if (dbRegions.length === 0) {
      return (
        <div className="p-4 text-sm text-muted-foreground text-center">No service areas configured yet</div>
      );
    }

    return (
      <div className="flex flex-col">
        {/* Promo highlight strip */}
        {highlightPromo && (
          <div className="px-4 py-2.5 bg-primary/10 border-b border-primary/20 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full">
              <Tag className="h-2.5 w-2.5" />
              {(highlightPromo as any).discount_value}{(highlightPromo as any).discount_type === 'percentage' ? '%' : '$'} OFF
            </span>
            <span className="text-xs font-medium text-foreground truncate">{(highlightPromo as any).title}</span>
            {(highlightPromo as any).promo_code && (
              <span className="ml-auto text-[10px] font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded">{(highlightPromo as any).promo_code}</span>
            )}
          </div>
        )}

        <div className="flex min-h-[320px]">
          {/* Left sidebar — Regions */}
          <div className="w-[200px] border-r border-border/50 p-2 flex flex-col bg-muted/30">
            <p className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">Regions</p>
            <div className="space-y-0.5 flex-1 overflow-y-auto max-h-[280px]">
              {regionsWithLocs.map((region: any) => {
                const count = dbLocations.filter((l: any) => l.region_id === region.id).length;
                return (
                  <button
                    key={region.id}
                    onMouseEnter={() => setHoveredRegion(region.id)}
                    onClick={() => { closeMenu(); window.location.href = `/${industrySlug}/${region.slug}`; }}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm rounded-lg transition-all text-left',
                      activeRegionId === region.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground/80 hover:bg-muted/50 hover:text-foreground'
                    )}
                  >
                    <span className="flex items-center gap-2 truncate">
                      <DynamicIcon name={region.icon || 'MapPin'} className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{region.name}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-[10px] text-muted-foreground">{count}</span>
                      <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="pt-2 mt-2 border-t border-border/50">
              <Link to="/service-areas" onClick={closeMenu} className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-primary hover:bg-muted/50 rounded-lg transition-colors">
                <MapPin className="h-3.5 w-3.5" />
                View All Regions
              </Link>
            </div>
          </div>

          {/* Right content — Service areas in selected region */}
          <div className="flex-1 p-4">
            {activeRegion && (
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Service Areas</p>
                <Link
                  to={`/${industrySlug}/${(activeRegion as any).slug}`}
                  onClick={closeMenu}
                  className="text-xs text-primary font-medium hover:underline flex items-center gap-0.5"
                >
                  View All <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            )}
            <div className="grid grid-cols-2 gap-1">
              {displayLocs.map((loc: any) => (
                <Link
                  key={loc.slug}
                  to={`/${industrySlug}/${(activeRegion as any)?.slug || 'area'}/${loc.slug}`}
                  onClick={closeMenu}
                  className={dropdownItemClasses}
                  role="menuitem"
                >
                  <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-all">
                    <MapPin className="h-3.5 w-3.5 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <span className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">{loc.name}</span>
                </Link>
              ))}
              {displayLocs.length === 0 && (
                <p className="col-span-2 px-3 py-4 text-sm text-muted-foreground">No service areas in this region yet.</p>
              )}
            </div>
            {hasMoreLocs && activeRegion && (
              <div className="mt-3 pt-3 border-t border-border/30">
                <Link
                  to={`/${industrySlug}/${(activeRegion as any).slug}`}
                  onClick={closeMenu}
                  className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  View all {regionLocs.length} service areas <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAboutDropdown = () => (
    <div className="p-2">
      <Link to="/about" onClick={closeMenu} className={dropdownItemClasses}>
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
          <Users className="h-4 w-4 text-primary group-hover:text-primary-foreground transition-colors" />
        </div>
        <div>
          <span className="font-medium text-sm text-foreground">About Us</span>
          <span className="text-xs text-muted-foreground block">Our story & team</span>
        </div>
      </Link>
      <Link to="/careers" onClick={closeMenu} className={dropdownItemClasses}>
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
          <Briefcase className="h-4 w-4 text-primary group-hover:text-primary-foreground transition-colors" />
        </div>
        <div>
          <span className="font-medium text-sm text-foreground">Careers</span>
          <span className="text-xs text-muted-foreground block">Join our team</span>
        </div>
      </Link>
      {dynamicPages.map(page => (
        <Link key={page.id} to={`/${page.slug}`} onClick={closeMenu} className={dropdownItemClasses}>
          <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary-foreground transition-colors" />
          </div>
          <div>
            <span className="font-medium text-sm text-foreground">{page.title}</span>
          </div>
        </Link>
      ))}
    </div>
  );

  const getDropdownContent = (item: NavItem) => {
    if (item.id === 'services') return { width: 'w-[520px]', content: renderServicesDropdown() };
    if (item.id === 'areas') return { width: 'w-[620px]', content: renderAreasDropdown() };
    if (item.id === 'about') return { width: 'w-[240px]', content: renderAboutDropdown() };
    return null;
  };

  const getItemActiveState = (item: NavItem) => {
    if (item.id === 'services') return isActive('/services') || isActivePrefix('/residential') || isActivePrefix('/commercial');
    if (item.id === 'areas') return isActive(`/${industrySlug}`) || isActivePrefix(`/${industrySlug}/`);
    if (item.id === 'about') return isActive('/about') || isActive('/careers');
    if (item.href) return isActive(item.href);
    return false;
  };

  const navItemClasses = (active: boolean) =>
    `flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg ${
      active ? 'text-primary bg-primary/5' : 'text-foreground/80 hover:text-foreground hover:bg-muted/50'
    }`;

  // ─── Logo element ───
  const LogoElement = () => (
    <Link to="/" className="flex items-center gap-2.5 font-heading font-bold text-lg tracking-tight" onClick={closeMenu}>
      {logoUrl ? (
        <img src={logoUrl} alt={companyName} className="h-9 w-auto object-contain" />
      ) : (
        <span className="text-foreground">{companyName}</span>
      )}
      {logoUrl && <span className="hidden sm:inline text-foreground">{companyName}</span>}
    </Link>
  );

  // ─── Desktop nav items with dropdowns ───
  const DesktopNav = () => (
    <nav className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Main navigation">
      {visibleItems.map(item => {
        if (item.type === 'dropdown') {
          const dropdown = getDropdownContent(item);
          if (!dropdown) {
            // Custom dropdown item without built-in content — render as link
            return (
              <Link key={item.id} to={item.href || '/'} className={navItemClasses(getItemActiveState(item))} onClick={closeMenu}>
                {item.icon && <DynamicIcon name={item.icon} className="h-4 w-4" />}
                {item.label}
              </Link>
            );
          }
          return (
            <div key={item.id} className="relative" onMouseEnter={() => setActiveMenu(item.id)} onMouseLeave={() => setActiveMenu(null)}>
              <button className={navItemClasses(getItemActiveState(item))} aria-expanded={activeMenu === item.id} aria-haspopup="true">
                {item.icon && <DynamicIcon name={item.icon} className="h-4 w-4" />}
                {item.label}
                <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', activeMenu === item.id && 'rotate-180')} />
              </button>
              <AnimatePresence>
                {activeMenu === item.id && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className={`${dropdownClasses} ${dropdown.width}`}
                    role="menu"
                  >
                    {dropdown.content}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        }
        return (
          <Link key={item.id} to={item.href || '/'} className={navItemClasses(getItemActiveState(item))} onClick={closeMenu}>
            {item.icon && <DynamicIcon name={item.icon} className="h-4 w-4" />}
            {item.label}
          </Link>
        );
      })}
    </nav>
  );

  // ─── Mobile menu ───
  const MobileNav = () => (
    <AnimatePresence>
      {isMobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden border-t border-border bg-background overflow-hidden"
        >
          <div className="container mx-auto px-4 py-4 max-h-[70vh] overflow-y-auto space-y-1">
            {visibleItems.map(item => {
              if (item.type === 'dropdown' && item.id === 'services' && dbSegments.length > 0) {
                return (
                  <div key={item.id} className="space-y-1">
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      {item.icon && <DynamicIcon name={item.icon} className="h-3.5 w-3.5" />}
                      {item.label}
                    </div>
                    {dbSegments.map((seg: any) => {
                      const segCats = dbCategories.filter((c: any) => c.segment_id === seg.id);
                      return segCats.map((cat: any) => (
                        <Link key={cat.id} to={`/${seg.slug}/${cat.slug}`} onClick={closeMenu} className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 rounded-lg">
                          {cat.name}
                        </Link>
                      ));
                    })}
                    <Link to="/services" onClick={closeMenu} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary">
                      View All <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                );
              }
              if (item.type === 'dropdown' && item.id === 'areas' && dbRegions.length > 0) {
                return (
                  <div key={item.id} className="space-y-1 pt-2 border-t border-border/50">
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> {item.label}
                    </div>
                    {dbRegions.slice(0, 3).map((region: any) => {
                      const regionLocs = dbLocations.filter((l: any) => l.region_id === region.id);
                      return regionLocs.slice(0, 4).map((loc: any) => (
                        <Link key={loc.slug} to={`/${industrySlug}/${region.slug}/${loc.slug}`} onClick={closeMenu} className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted/50 rounded-lg">
                          {loc.name}
                        </Link>
                      ));
                    })}
                    <Link to={`/${industrySlug}`} onClick={closeMenu} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary">
                      View All <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                );
              }
              return (
                <Link key={item.id} to={item.href || '/'} onClick={closeMenu} className="block px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg">
                  {item.label}
                </Link>
              );
            })}
            <Link to="/admin/auth" onClick={closeMenu} className="block px-4 py-3 text-sm text-muted-foreground hover:bg-muted/50 rounded-lg">Admin</Link>
            <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center justify-center gap-2 px-4 py-3 mt-2 rounded-full bg-primary text-primary-foreground text-sm font-medium">
              <Phone className="h-4 w-4" /> {phone}
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // ─── Render by header style ───
  const showUtilityBar = cfg.utility_bar && (config.header_style === 'bold' || config.header_style === 'mega' || config.header_style === 'split');

  const UtilityBar = () => (
    showUtilityBar ? (
      <div className="hidden lg:block text-sm bg-foreground text-background">
        <div className="container mx-auto px-4 flex items-center justify-between h-9">
          <div className="flex items-center gap-4 text-xs">
            {cfg.utility_badges.map((badge, i) => (
              <span key={i} className="flex items-center gap-1 text-background/70"><Shield className="h-3 w-3" /> {badge}</span>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs text-background/70">
            <Link to="/admin/auth" className="hover:text-background transition-opacity">Sign In</Link>
            {cfg.show_phone_in_nav && (
              <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-1.5 font-semibold text-background hover:text-background/80 transition-opacity">
                <Phone className="h-3 w-3" /> {phone}
              </a>
            )}
          </div>
        </div>
      </div>
    ) : null
  );

  const MobileToggle = () => (
    <button onClick={() => setIsMobileOpen(!isMobileOpen)} className="lg:hidden p-2 text-foreground hover:text-primary transition-colors rounded-lg hover:bg-muted/50" aria-label="Toggle menu">
      {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
    </button>
  );

  // Calculate spacer height based on header style
  const getSpacerHeight = () => {
    if (config.header_style === 'centered') return showUtilityBar ? 'h-44' : 'h-36';
    if (config.header_style === 'bold') return showUtilityBar ? 'h-28' : 'h-20';
    if (config.header_style === 'minimal') return 'h-14';
    // default/split/mega
    return showUtilityBar ? 'h-28 lg:h-32' : 'h-16 lg:h-20';
  };

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-50">
      <UtilityBar />

      {config.header_style === 'centered' ? (
        /* ─── CENTERED: Logo top-center, nav below ─── */
        <nav className={cn('bg-card/95 backdrop-blur-xl border-b border-border/60 shadow-sm transition-all duration-300', isScrolled && 'shadow-md')}>
          <div className="container mx-auto px-4">
            <div className="hidden lg:flex items-center justify-between h-10 text-sm text-muted-foreground border-b border-border/30">
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Serving Your Area</span>
              <div className="flex items-center gap-4">
                <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-1 hover:text-foreground"><Phone className="h-3.5 w-3.5" /> {phone}</a>
                <Link to="/admin/auth" className="hover:text-foreground">Admin</Link>
              </div>
            </div>
            <div className="flex flex-col items-center py-3">
              <LogoElement />
            </div>
            <div className="hidden lg:flex items-center justify-center gap-1 pb-3">
              <DesktopNav />
              <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/contact">{cfg.cta_text}</Link>
              </Button>
            </div>
            <div className="lg:hidden flex items-center justify-end px-4 pb-3"><MobileToggle /></div>
          </div>
          <MobileNav />
        </nav>

      ) : config.header_style === 'bold' ? (
        /* ─── BOLD: Dark bg, larger logo, prominent CTA ─── */
        <nav className={cn('border-b border-border/60 transition-all duration-300 bg-foreground text-background', isScrolled ? 'shadow-lg' : '')}
          >
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between h-18 lg:h-22 py-2">
              <Link to="/" className="flex items-center gap-3 font-heading font-bold text-xl text-background tracking-tight" onClick={closeMenu}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} className="h-11 w-auto object-contain" />
                ) : null}
                <span className="hidden sm:inline">{companyName}</span>
              </Link>
              <nav className="hidden lg:flex items-center gap-1" role="navigation">
                {visibleItems.map(item => {
                  if (item.type === 'dropdown') {
                    const dropdown = getDropdownContent(item);
                    if (!dropdown) return (
                      <Link key={item.id} to={item.href || '/'} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all rounded-lg ${getItemActiveState(item) ? 'text-white bg-white/10' : 'text-white/70 hover:text-white hover:bg-white/5'}`} onClick={closeMenu}>
                        {item.icon && <DynamicIcon name={item.icon} className="h-4 w-4" />}{item.label}
                      </Link>
                    );
                    return (
                      <div key={item.id} className="relative" onMouseEnter={() => setActiveMenu(item.id)} onMouseLeave={() => setActiveMenu(null)}>
                        <button className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all rounded-lg ${getItemActiveState(item) ? 'text-white bg-white/10' : 'text-white/70 hover:text-white hover:bg-white/5'}`}>
                          {item.icon && <DynamicIcon name={item.icon} className="h-4 w-4" />}{item.label}
                          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', activeMenu === item.id && 'rotate-180')} />
                        </button>
                        <AnimatePresence>{activeMenu === item.id && (
                          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
                            className={`${dropdownClasses} ${dropdown.width}`} role="menu">{dropdown.content}</motion.div>
                        )}</AnimatePresence>
                      </div>
                    );
                  }
                  return (
                    <Link key={item.id} to={item.href || '/'} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all rounded-lg ${getItemActiveState(item) ? 'text-white bg-white/10' : 'text-white/70 hover:text-white hover:bg-white/5'}`} onClick={closeMenu}>
                      {item.icon && <DynamicIcon name={item.icon} className="h-4 w-4" />}{item.label}
                    </Link>
                  );
                })}
              </nav>
              <div className="flex items-center gap-3">
                <Button asChild className="hidden sm:inline-flex text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-full">
                  <a href={`tel:${phone.replace(/\D/g, '')}`}>
                    <Phone className="h-4 w-4 mr-1.5" /> {cfg.cta_text}
                  </a>
                </Button>
                <MobileToggle />
              </div>
            </div>
          </div>
          <MobileNav />
        </nav>

      ) : config.header_style === 'minimal' ? (
        /* ─── MINIMAL: Thin, light, subtle nav ─── */
        <nav className={cn('bg-background/80 backdrop-blur-xl transition-all duration-300', isScrolled ? 'shadow-sm border-b border-border/40' : '')}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between h-14">
              <LogoElement />
              <DesktopNav />
              <div className="flex items-center gap-3">
                <Link to="/admin/auth" className="text-xs text-muted-foreground hover:text-foreground transition-colors hidden sm:inline-flex">Admin</Link>
                <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex text-sm">
                  <a href={`tel:${phone.replace(/\D/g, '')}`}><Phone className="h-3.5 w-3.5 mr-1" /> {phone}</a>
                </Button>
                <MobileToggle />
              </div>
            </div>
          </div>
          <MobileNav />
        </nav>

      ) : (
        /* ─── DEFAULT (split / mega): Standard layout ─── */
        <nav className={cn('bg-card/95 backdrop-blur-xl border-b border-border/60 transition-all duration-300', isScrolled && 'shadow-md')}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between h-16 lg:h-20">
              <LogoElement />
              <DesktopNav />
              <div className="flex items-center gap-3">
                {!showUtilityBar && (
                  <Link to="/admin/auth" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:inline-flex">Admin</Link>
                )}
                <Button asChild className="hidden sm:inline-flex text-sm bg-foreground text-background hover:bg-foreground/90 rounded-full">
                  <a href={`tel:${phone.replace(/\D/g, '')}`}>
                    <Phone className="h-4 w-4 mr-1.5" /> {cfg.cta_text}
                  </a>
                </Button>
                <MobileToggle />
              </div>
            </div>
          </div>
          <MobileNav />
        </nav>
      )}
    </header>
    {/* Spacer to prevent content from hiding behind fixed header */}
    <div className={getSpacerHeight()} />
    </>
  );
};
